import { Box, Divider, FormField, Input, Text } from "@wix/design-system";
import React, { useContext } from "react";
import { RicosContext, usePluginExportsContext } from "./../../ricos-context/index.js";
import { useBiNext } from "./../../_shared/bi-context-provider/bi-context-provider.js";
import { useSpoilerPlugin } from "./../../_shared/use-spoiler-plugin.js";
import {
  ImageNew as Image,
  ToggleButton,
  usePanelContainerElement
} from "./../../ui-components/index.js";
import { IMAGE_SETTINGS_MODAL_ID } from "../consts.js";
import {
  SETTINGS_ALT_INPUT,
  SETTINGS_DISABLE_DOWNLOAD_TOGGLE,
  SETTINGS_DISABLE_EXPAND_TOGGLE,
  SETTINGS_IMAGE_PREVIEW,
  SETTINGS_IS_DECORATIVE_TOGGLE,
  SETTINGS_SPOILER_TOGGLE
} from "../data-hooks.js";
import { IMAGE_TYPE } from "../types.js";
const SettingsTab = ({ pmNodeAttrs, patchPmNodeAttrs }) => {
  const { t, environment } = useContext(RicosContext);
  const containerEl = usePanelContainerElement();
  const exports = usePluginExportsContext();
  const isSpoilerEnabled = useSpoilerPlugin(IMAGE_TYPE);
  const logBi = useBiNext({
    modalId: IMAGE_SETTINGS_MODAL_ID,
    pluginId: "image",
    nodeId: pmNodeAttrs?.id
  });
  const AltTextComponent = exports?.aiIntegrations?.image?.altTextInput;
  const altText = pmNodeAttrs.altText ?? "";
  return /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP3", padding: "SP3", overflow: "auto" }, /* @__PURE__ */ React.createElement(
    Image,
    {
      dataHook: SETTINGS_IMAGE_PREVIEW,
      alt: altText || t("ImagePlugin_Image_Preview_Alt"),
      src: pmNodeAttrs.image?.src?.id || pmNodeAttrs.image?.src?.url || "",
      width: Number(pmNodeAttrs.image?.width),
      height: Number(pmNodeAttrs.image?.height),
      requiredHeight: 156,
      requiredWidth: 252,
      borderRadius: environment === "businessManager" ? 8 : 0
    }
  ), AltTextComponent ? /* @__PURE__ */ React.createElement(
    AltTextComponent,
    {
      image: pmNodeAttrs.image,
      altText,
      onChange: (value) => {
        patchPmNodeAttrs({ altText: value });
      },
      tooltipAppendTo: containerEl,
      logBi,
      disabled: Boolean(pmNodeAttrs.decorative)
    }
  ) : /* @__PURE__ */ React.createElement(
    FormField,
    {
      label: t("ImageSettings_Alt_Label"),
      infoContent: /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP1" }, /* @__PURE__ */ React.createElement(Text, { light: true, size: "small" }, t("ImageSettings_Alt_Label_Tooltip"))),
      infoTooltipProps: {
        size: "small",
        appendTo: containerEl
      }
    },
    /* @__PURE__ */ React.createElement(
      Input,
      {
        dataHook: SETTINGS_ALT_INPUT,
        type: "text",
        placeholder: t("ImageSettings_Alt_Input_Placeholder"),
        value: altText,
        onChange: (e) => {
          logBi({ dataHook: SETTINGS_ALT_INPUT, value: e.target.value });
          patchPmNodeAttrs({ altText: e.target.value });
        },
        size: "small",
        textOverflow: "ellipsis",
        disabled: Boolean(pmNodeAttrs.decorative)
      }
    )
  ), /* @__PURE__ */ React.createElement(
    ToggleButton,
    {
      title: t("ImagePlugin_Settings_Is_Decorative"),
      checked: Boolean(pmNodeAttrs.decorative),
      onChange: () => {
        logBi({
          dataHook: SETTINGS_IS_DECORATIVE_TOGGLE,
          value: Boolean(pmNodeAttrs.decorative)
        });
        patchPmNodeAttrs({
          decorative: !pmNodeAttrs.decorative,
          altText: void 0
        });
      },
      infoContent: t("ImagePlugin_Settings_Is_Decorative_Tooltip"),
      dataHook: SETTINGS_IS_DECORATIVE_TOGGLE,
      size: "small",
      tooltipAppendTo: containerEl
    }
  ), /* @__PURE__ */ React.createElement(Box, { marginInline: "-18px" }, /* @__PURE__ */ React.createElement(Divider, null)), /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold" }, t("ImagePlugin_Settings_Section_Options")), /* @__PURE__ */ React.createElement(
    ToggleButton,
    {
      title: t("ImagePlugin_Settings_ImageOpensInExpandMode_Label"),
      checked: !pmNodeAttrs.disableExpand,
      onChange: () => {
        logBi({
          dataHook: SETTINGS_DISABLE_EXPAND_TOGGLE,
          value: !!pmNodeAttrs.disableExpand
        });
        patchPmNodeAttrs({
          disableExpand: !pmNodeAttrs.disableExpand
        });
      },
      infoContent: t("ImageSettings_Expand_Mode_Toggle"),
      dataHook: SETTINGS_DISABLE_EXPAND_TOGGLE,
      size: "small",
      tooltipAppendTo: containerEl
    }
  ), /* @__PURE__ */ React.createElement(
    ToggleButton,
    {
      title: t("ImagePlugin_Settings_ImageCanBeDownloaded_Label"),
      checked: !pmNodeAttrs.disableDownload,
      onChange: () => {
        logBi({
          dataHook: SETTINGS_DISABLE_DOWNLOAD_TOGGLE,
          value: !!pmNodeAttrs.disableDownload
        });
        patchPmNodeAttrs({
          disableDownload: !pmNodeAttrs.disableDownload
        });
      },
      infoContent: t("ImagePlugin_Settings_ImageCanBeDownloaded_Tooltip"),
      dataHook: SETTINGS_DISABLE_DOWNLOAD_TOGGLE,
      size: "small",
      tooltipAppendTo: containerEl
    }
  ), isSpoilerEnabled ? /* @__PURE__ */ React.createElement(
    ToggleButton,
    {
      title: t("ImageSettings_Spoiler_Toggle"),
      checked: Boolean(pmNodeAttrs.containerData?.spoiler?.enabled),
      onChange: () => {
        logBi({
          dataHook: SETTINGS_SPOILER_TOGGLE,
          value: !pmNodeAttrs.containerData?.spoiler?.enabled
        });
        patchPmNodeAttrs({
          containerData: {
            ...pmNodeAttrs.containerData,
            spoiler: {
              ...pmNodeAttrs.containerData?.spoiler,
              enabled: !pmNodeAttrs.containerData?.spoiler?.enabled
            }
          }
        });
      },
      infoContent: t("Spoiler_Toggle_Tooltip"),
      dataHook: SETTINGS_SPOILER_TOGGLE,
      size: "small",
      tooltipAppendTo: containerEl
    }
  ) : null);
};
export {
  SettingsTab
};
//# sourceMappingURL=settings-tab.js.map
